import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import errorLogger, {
  BUGSNAG_STATUSES,
} from '@picsart/growth-rc/services/errorLogger';

import { useAppStore, setAppStore, getAppStore, type IAppStore } from '@/store';
import { ToastType } from '@/store/toast/types';
import { imageAccept, videoAccept } from '@/consts/configs';
import useAuth from '@/hooks/useAuth';
import useFileUpload from '@/hooks/useFileUpload';
import useGenerate from '@/hooks/useGenerate';
import { useMediaHandlers } from '@/hooks/useMediaHandlers';
import useGenerateAction from '@/hooks/useGenerateAction';
import getOrFetchQuery from '@/utils/query/getOrFetchQuery';
import getLibraryItemInfo from '@/requests/library/getLibraryItemInfo';
import buildInputsFromPreset from '@/utils/preset/buildInputsFromPreset';
import isFormValid from '@/utils/isFormValid';
import revokeBlobUrl from '@/utils/file/revokeBlobUrl';
import { getInvalidTitle } from '@/utils/upload/getUploadErrorMessages';
import isNativeMobile from '@/utils/platform/isNativeMobile';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import { AiGenScreenAction, ClickItem, ScreenType } from '@/types/analytics';
import { InputType } from '@/types';

import type { Preset, IInput, IMediaInput } from '@/types';

const selector = (store: IAppStore) => ({
  inputs: store.inputs,
  isPresetLoading: store.isPresetLoading,
  pendingUploads: store.pendingUploads,
  showToast: store.showToast,
  updateInput: store.updateInput,
});

const getMediaInputs = (inputs: IInput[]): IMediaInput[] =>
  inputs.filter((input): input is IMediaInput => input.type !== InputType.TEXT);

interface AwaitingUpload {
  inputId: string;
  slug: string;
}

const usePresetFeedActions = () => {
  const queryClient = useQueryClient();
  const { uploadMedia } = useGenerate();
  const mediaHandlers = useMediaHandlers({ uploadMedia });
  const { handleGenerate, isSubmitting } = useGenerateAction();
  const { isAuthorized } = useAuth();
  const { inputs, isPresetLoading, pendingUploads, showToast, updateInput } =
    useAppStore(selector);
  const isOnNativeMobile = isNativeMobile();

  // The upload we opened the picker for. Carries the slide's slug so an
  // upload that lands after the user swiped away (or input ids colliding
  // across presets) can be recognized and discarded instead of generating
  // under the wrong slide.
  const awaitingUploadRef = useRef<AwaitingUpload | null>(null);
  // A pick that hit the auth gate. Mirrors the pendingDriveAction pattern:
  // consumed by the post-login resume effect below once the parked upload
  // has swapped the blob preview for its CDN url.
  const pendingAuthGenerateRef = useRef<AwaitingUpload | null>(null);
  // A sheet Generate that hit the auth gate. Stores the slide's slug so the
  // post-login resume below can re-fire generation once uploads have settled.
  // The sheet can carry many media inputs (or none), so the slug alone — not a
  // single {inputId, slug} — identifies the pending intent.
  const pendingSheetGenerateRef = useRef<string | null>(null);
  // Last-request-wins guard: tracks the most recently requested preset slug.
  // Nulled on unmount so a fetch resolving after the feed closes can't
  // overwrite the Create screen's store state.
  const activeSlugRef = useRef<string | null>(null);
  // Blob preview URL created for a logged-out direct-picker upload. On the happy
  // path uploadMedia (post-login) and handleMediaRemove (swipe/re-pick) already
  // revoke it; this ref lets the unmount cleanup revoke it too when the user
  // abandons the pick (dismisses auth / closes the feed) before either runs.
  const previewBlobUrlRef = useRef<string | null>(null);
  const [shouldGenerate, setShouldGenerate] = useState(false);
  const [isInputsSheetOpen, setIsInputsSheetOpen] = useState(false);

  const mediaInputs = useMemo(() => getMediaInputs(inputs), [inputs]);
  const hasTextInputs = inputs.some((input) => input.type === InputType.TEXT);
  // The only preset shape whose Recreate tap opens the picker directly:
  // exactly one media input and nothing to type. Every other shape fills
  // its inputs through the inputs sheet.
  const directPickerInput =
    mediaInputs.length === 1 && !hasTextInputs ? mediaInputs[0] : null;

  // The app-standard web file-picker machinery (same stack UploadArea uses):
  // owns the hidden input's ref/change handler, validates the file type, and
  // manages the blob-preview lifecycle. The feed has no dropzone UI, so
  // invalid files surface through the global toast.
  const accept =
    directPickerInput?.type === InputType.VIDEO ? videoAccept : imageAccept;
  const filePicker = useFileUpload({
    accept,
    value: directPickerInput?.src,
    hasUploadError: directPickerInput?.hasUploadError,
    onUpload: (file) => {
      const awaiting = awaitingUploadRef.current;
      if (!awaiting || activeSlugRef.current !== awaiting.slug) return false;
      // Mirror the native direct-picker: for a logged-out user, a blob preview in
      // the store `src` is what arms the auto-generate → registration-modal →
      // post-login resume chain (it gates on directPickerInput.src).
      // handleMediaUpload parks the file for the post-login CDN upload.
      if (!isAuthorized) {
        // Revoke any prior abandoned preview, then track the new blob so unmount
        // cleanup can free it if this pick is abandoned before login/removal.
        revokeBlobUrl(previewBlobUrlRef.current);
        const previewUrl = URL.createObjectURL(file);
        previewBlobUrlRef.current = previewUrl;
        updateInput({
          id: awaiting.inputId,
          value: {
            src: previewUrl,
            isLoading: false,
            hasUploadError: false,
          },
        });
      }
      return mediaHandlers.handleMediaUpload(awaiting.inputId, file);
    },
    showInvalidFeedback: () =>
      showToast(getInvalidTitle({ accept }), ToastType.Error),
  });

  const loadActivePreset = useCallback(
    async (slug: string) => {
      activeSlugRef.current = slug;
      setIsInputsSheetOpen(false);
      setAppStore({ isPresetLoading: true });
      try {
        const preset = await getOrFetchQuery<Preset>({
          key: ['libraryItemInfo', slug],
          queryClient,
          fetcher: async () => {
            const data = await getLibraryItemInfo(slug);
            if (!data) throw new Error('Failed to load library item');
            return data;
          },
        });
        const built = buildInputsFromPreset(preset);
        // Fresh start for the feed: clear any preserved media src.
        const inputsForFeed = built.map((input) =>
          input.type === InputType.TEXT ? input : { ...input, src: '' }
        );
        if (activeSlugRef.current !== slug) return;
        setAppStore({
          selectedPreset: { ...preset, slug },
          inputs: inputsForFeed,
          isPresetLoading: false,
        });
      } catch (error) {
        errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN);
        if (activeSlugRef.current === slug) {
          setAppStore({ isPresetLoading: false });
        }
      }
    },
    [queryClient]
  );

  const openPickerForInput = useCallback(
    (input: IMediaInput) => {
      const slug = activeSlugRef.current;
      if (!slug) return;
      awaitingUploadRef.current = { inputId: input.id, slug };
      if (isOnNativeMobile) {
        mediaHandlers.handleSelectFromNativePicker(input.id, input.type);
      } else {
        // Web: the click stays in the gesture — openFilePicker is synchronous.
        filePicker.openFilePicker();
      }
    },
    [mediaHandlers, isOnNativeMobile, filePicker]
  );

  const handleUploadTap = useCallback(() => {
    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.CLICK,
      screen_type: ScreenType.PRESET_FEED,
      item: ClickItem.Effect,
      settings: {
        category: getAppStore().activeTabSlug ?? '',
        id: activeSlugRef.current ?? '',
      },
    });
    if (directPickerInput) {
      if (directPickerInput.src) {
        mediaHandlers.handleMediaRemove(directPickerInput.id);
      }
      openPickerForInput(directPickerInput);
      return;
    }
    setIsInputsSheetOpen(true);
  }, [directPickerInput, openPickerForInput, mediaHandlers]);

  const closeSheet = useCallback(() => {
    setIsInputsSheetOpen(false);
  }, []);

  const handleSheetGenerate = useCallback(() => {
    if (isSubmitting) return;
    if (isAuthorized) {
      // Authorized: generate now and dismiss the sheet.
      setIsInputsSheetOpen(false);
    } else {
      // Logged out: keep the sheet open behind the registration modal and arm
      // the post-login resume so a successful login generates without the user
      // re-tapping Recreate. The dispatch below opens the registration modal.
      pendingSheetGenerateRef.current = activeSlugRef.current;
    }
    handleGenerate().catch((error) =>
      errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN)
    );
  }, [handleGenerate, isSubmitting, isAuthorized]);

  useEffect(() => {
    if (!directPickerInput?.src) return;
    const awaiting = awaitingUploadRef.current;
    if (!awaiting || awaiting.inputId !== directPickerInput.id) return;

    if (isAuthorized && !isFormValid(inputs)) return;
    awaitingUploadRef.current = null;
    // The upload belongs to the slide it was picked on. If the user swiped
    // away while it uploaded, discard it — generating another slide's preset
    // with this photo (or leaving it stuck in that slide's slot) is worse.
    if (awaiting.slug !== activeSlugRef.current) {
      mediaHandlers.handleMediaRemove(directPickerInput.id);
      return;
    }
    // Logged out, the file is parked in pendingUploads with a blob preview.
    // Still dispatch (the auth gate opens the registration modal) but arm the
    // post-login resume so a successful login picks the flow back up.
    if (!isAuthorized) pendingAuthGenerateRef.current = awaiting;
    setShouldGenerate(true);
  }, [directPickerInput, mediaHandlers, isAuthorized, inputs]);

  // Post-login resume for the direct-picker flow. Waits for usePendingUploads
  // to finish the deferred CDN upload — while the input still sits in
  // pendingUploads (or is mid-upload) the src is a blob: url that must never
  // reach the backend.
  useEffect(() => {
    if (!isAuthorized) return;
    const pending = pendingAuthGenerateRef.current;
    if (!pending) return;
    // Swiped to another slide during login — the photo no longer belongs to
    // the active preset.
    if (pending.slug !== activeSlugRef.current) {
      pendingAuthGenerateRef.current = null;
      return;
    }
    if (!directPickerInput || directPickerInput.id !== pending.inputId) return;
    if (!directPickerInput.src || directPickerInput.isLoading) return;
    if (pendingUploads.some((upload) => upload.id === pending.inputId)) return;
    pendingAuthGenerateRef.current = null;
    setShouldGenerate(true);
  }, [isAuthorized, directPickerInput, pendingUploads]);

  // Post-login resume for the inputs-sheet flow (every preset shape that isn't
  // the single-media direct picker: prompt-only, image + prompt, multi-image,
  // video). The sheet lets a logged-out user tap Generate on a partially filled
  // form (the showSignIn bypass), so gate the resume on the strict isFormValid:
  // fire only once usePendingUploads has swapped every blob preview for its CDN
  // url and all inputs are valid. Prompt-only presets have no media to wait on.
  useEffect(() => {
    if (!isAuthorized) return;
    const pendingSlug = pendingSheetGenerateRef.current;
    if (!pendingSlug) return;
    // Swiped to another slide during login — the intent no longer applies.
    if (pendingSlug !== activeSlugRef.current) {
      pendingSheetGenerateRef.current = null;
      return;
    }
    if (pendingUploads.length > 0) return;
    if (!isFormValid(inputs)) return;
    pendingSheetGenerateRef.current = null;
    setShouldGenerate(true);
  }, [isAuthorized, pendingUploads, inputs]);

  // Invalidate in-flight work when the feed closes: a preset fetch or upload
  // resolving after unmount must not touch the store the Create screen uses.
  useEffect(
    () => () => {
      activeSlugRef.current = null;
      awaitingUploadRef.current = null;
      pendingAuthGenerateRef.current = null;
      pendingSheetGenerateRef.current = null;
      revokeBlobUrl(previewBlobUrlRef.current);
      previewBlobUrlRef.current = null;
    },
    []
  );

  useEffect(() => {
    if (!shouldGenerate) return;
    setShouldGenerate(false);
    handleGenerate().catch((error) =>
      errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN)
    );
  }, [shouldGenerate, handleGenerate]);

  // Disabled while the slide's preset is still loading (the store would hand
  // the sheet the previous slide's inputs), during the direct-picker flow's
  // CDN upload (re-tapping would start a duplicate upload), and while a
  // generate dispatch runs (isSubmitting spans auth gate → credits gate →
  // fire). A filled slot stays tappable: the credits gate may have refused
  // the auto-generate, and re-picking is the retry path.
  const isUploadDisabled =
    isPresetLoading || !!directPickerInput?.isLoading || isSubmitting;

  const isUploadLoading = !!directPickerInput?.isLoading || isSubmitting;

  return {
    loadActivePreset,
    handleUploadTap,
    isUploadDisabled,
    isUploadLoading,
    isInputsSheetOpen,
    closeSheet,
    handleSheetGenerate,
    isGenerating: isSubmitting,
    filePickerInputProps: {
      inputRef: filePicker.inputRef,
      accept,
      onChange: filePicker.handleChange,
    },
  };
};

export default usePresetFeedActions;
